import { useEffect } from 'react';
import { ngRoute } from '../../../app/routing/ngRoute';
import { getApiHost } from '../utils/getApiHost';
import { Model, switchExperience } from './switchExperience/switchExperience';

const MIGRATE_STATUS_POLL_INTERVAL_MS = 30000;

type MigrationStatus = 'MIGRATED' | 'MIGRATING' | 'UNMIGRATED';

export interface ModernDashboardsRedirectProps {
    model: Model;
}

export function ModernDashboardsRedirect({ model }: ModernDashboardsRedirectProps): JSX.Element {
    useEffect(() => {
        if (ngRoute.history?.location.pathname.split('/').at(1) !== 'dashboard') {
            return undefined;
        }

        let isCurrent = true;
        let migrationStatusPollInterval: number | undefined;

        function stopPolling(): void {
            if (migrationStatusPollInterval !== undefined) {
                window.clearInterval(migrationStatusPollInterval);
                migrationStatusPollInterval = undefined;
            }
        }

        function startPolling(): void {
            if (migrationStatusPollInterval === undefined) {
                migrationStatusPollInterval = window.setInterval(() => {
                    void checkAndHandleStatus();
                }, MIGRATE_STATUS_POLL_INTERVAL_MS);
            }
        }

        async function checkAndHandleStatus(): Promise<void> {
            const status = await fetchMigrationStatus();

            if (!isCurrent) {
                return;
            }

            if (status === 'MIGRATED') {
                stopPolling();
                switchExperience(model);
            } else if (status === 'MIGRATING') {
                startPolling();
            } else {
                stopPolling();
            }
        }

        void checkAndHandleStatus();

        return (): void => {
            isCurrent = false;
            stopPolling();
        };
    }, [model]);

    return <></>;
}

async function fetchMigrationStatus(): Promise<MigrationStatus | undefined> {
    try {
        const response = await fetch(new URL('/v2/directory/~migrate/status', getApiHost()), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-SF-TOKEN': window.localStorage.getItem('X-SF-TOKEN') ?? '',
            },
        });

        if (!response.ok) {
            return undefined;
        }

        const json = (await response.json()) as {
            data: { status: MigrationStatus | undefined };
        };

        return json.data.status;
    } catch {
        return undefined;
    }
}
